// Pure helpers for editable link tooltips (see Components/LinkTooltips). Kept free of React and
// @cd2 imports so they can be unit tested.
//
// Tooltips are stored on globalSettings.linkTooltips as a map of key -> text. Keys look like
// "sidebar|/quotes" or "toolbar|/quotes/:id|Edit".

export type ILinkTooltips = Record<string, string>

export function sidebarTooltipKey(link: {
  route: string
  title: unknown
  links?: { route: string }[]
}) {
  // group headings without their own page use "#" as a route, and some borrow their first
  // sub-link's route (e.g. Order Requests -> Incoming), so fall back to the title for those
  if (link.route === "#" || link.links?.some(subLink => subLink.route === link.route)) {
    return typeof link.title === "string" ? `sidebar|#${link.title}` : undefined
  }
  return `sidebar|${link.route}`
}

// `path` is the matched route pattern (e.g. /quotes/:id) so a tooltip applies to that button on
// every record. Buttons without a plain text label need an explicit tooltipKey.
export function toolbarTooltipKey(path: string, item: { tooltipKey?: string; children?: unknown }) {
  const label = item.tooltipKey ?? (typeof item.children === "string" ? item.children : null)
  return label ? `toolbar|${path}|${label}` : undefined
}

export function describeTooltipKey(key: string) {
  const [area, path, label] = key.split("|")
  if (area === "sidebar") {
    return { area: "Sidebar", page: "", link: path }
  }
  if (area === "toolbar") {
    return { area: "Toolbar", page: path, link: label }
  }
  return { area: "", page: "", link: key }
}

// Returns a new map with the tooltip set, or removed when the content is blank
export function applyTooltipChange(
  tooltips: ILinkTooltips | null | undefined,
  key: string,
  content: string,
): ILinkTooltips {
  const next = { ...(tooltips ?? {}) }
  const trimmed = content.trim()
  if (trimmed) {
    next[key] = trimmed
  } else {
    delete next[key]
  }
  return next
}

export function canEditLinkTooltips(
  user: { id?: string | null; canEditLinkTooltips?: boolean; isSuperAdmin?: boolean } | null,
) {
  return !!user?.id && !!(user.canEditLinkTooltips || user.isSuperAdmin)
}
